import React, { useState, useEffect } from 'react';
import { Box, Container, Typography, Paper, Button, Stepper, Step, StepLabel, Divider } from '@mui/material';
import surveyApi from '../services/surveyApi';

// Import Modular Components
import PrivacyNoticeStep from '../steps/PrivacyNoticeStep';
import PersonalInfoStep from '../steps/PersonalInfoStep';
import ServiceAvailedStep from '../steps/ServiceAvailedStep';
import CitizenCharterStep from '../steps/CitizenCharterStep';
import ServiceQualityStep from '../steps/ServiceQualityStep';
import FeedbackStep from '../steps/FeedbackStep';
import ThankYouStep from '../steps/ThankYouStep';
import ErrorDialog from '../components/ErrorDialog';

// Import Theme and Data
import { themeColors } from '../theme/themeColors';
import { steps, serviceQualityQuestions } from '../data/formData';
import { subOffices } from '../data/formData';

const SurveyForm = () => {
  
  // All state variables
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState(null);
  const [submissionComplete, setSubmissionComplete] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const isThankYouStep = activeStep === steps.length || submissionComplete;
  const [clientType, setClientType] = useState('');
  const [InternalExternal, setInternalExternal] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState('');
  const [region, setRegion] = useState('');
  const [office, setOffice] = useState('');
  const [subOffice, setSubOffice] = useState('');
  const [service, setService] = useState('');
  const [otherSpecification, setOtherSpecification] = useState('');
  const [ccAwareness, setCCAwareness] = useState('');
  const [ccVisibility, setCCVisibility] = useState('');
  const [ccHelpfulness, setCCHelpfulness] = useState('');
  const [privacyAgreement, setPrivacyAgreement] = useState(false);
  const [satisfaction, setSatisfaction] = useState({
    sqd0: '',
    sqd1: '',
    sqd2: '',
    sqd3: '',
    sqd4: '',
    sqd5: '',
    sqd6: '',
    sqd7: '',
    sqd8: '',
    sqd9: '',
    sqd10: ''
  });

  // Timer related state variables
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);

  const [suggestions, setSuggestions] = useState('');
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState({
    clientType: false,
    gender: false,
    age: false,
    region: false,
    InternalExternal: false,
    office: false,
    subOffice: false,
    service: false,
    otherSpecification: false,
    ccAwareness: false,
    ccVisibility: false,
    ccHelpfulness: false,
    satisfaction: false,
    suggestions: false,
    email: false
  });
  
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const paymentRelatedServices = [
    'Room Accommodation/Function Hall',
    'Grant of certificate of compliance',
    'Application of Private Schools (SHS programs)',
    'Issuance of Bidding docs and RFQ',
    'Payment of External Claims',
    'Payment of Internal Claims'
  ];

  const isPaymentRelatedService = () => {
    return paymentRelatedServices.includes(service);
  };

  const validateEmail = (email) => {
    if (!email) return true; // Empty email is allowed (optional field)
    
    // Basic email validation regex pattern
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailPattern.test(email);
  };
  
  // Check if the selected office has sub-offices
  const hasSubOffices = () => {
    return office && subOffices[office] && subOffices[office].length > 0;
  };

  // Check if there are any negative ratings that would require feedback
  const hasNegativeRatings = () => {
    const negativeRatings = ['Strongly Disagree', 'Disagree', 'Neutral'];
    
    for (const [key, value] of Object.entries(satisfaction)) {
      // Skip SQD5 if service is not payment-related and it's set to N/A
      if (key === 'sqd5' && !isPaymentRelatedService() && value === 'N/A') {
        continue;
      }
      
      if (negativeRatings.includes(value)) {
        return true;
      }
    }
    
    return false;
  };

  // Start the timer function - no localStorage
  const startTimer = () => {
    const now = new Date().getTime();
    setStartTime(now);
    setTimerRunning(true);
  };
  
  // Stop the timer function
  const stopTimer = () => {
    if (timerRunning && startTime) {
      const now = new Date().getTime();
      const elapsed = now - startTime;
      setElapsedTime(elapsed);
      setTimerRunning(false);
      return elapsed; // Return the elapsed time for submission
    }
    return 0;
  };

  // Handle privacy agreement with timer start
  const handlePrivacyAgreement = (agreed) => {
    setPrivacyAgreement(agreed);
    if (agreed) {
      startTimer();
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    // If service is not payment-related, set SQD5 to "N/A"
    if (service && !isPaymentRelatedService()) {
      setSatisfaction(prev => ({
        ...prev,
        sqd5: 'N/A'
      }));
    } else if (isPaymentRelatedService() && satisfaction.sqd5 === 'N/A') {
      // If changing to a payment-related service and SQD5 was previously set to N/A, reset it
      setSatisfaction(prev => ({
        ...prev,
        sqd5: ''
      }));
    }
  }, [service, satisfaction.sqd5]);

  const showErrorModal = (message) => {
    setModalMessage(message);
    setOpenModal(true);
  };

  // Validate feedback step
  const validateFeedbackStep = () => {
    let hasErrors = false;
    let errorMessage = '';

    // Check if suggestions are required (due to negative ratings) but not provided
    if (hasNegativeRatings() && !suggestions) {
      setErrors(prev => ({ ...prev, suggestions: true }));
      errorMessage = "Please provide feedback on why you gave negative ratings. Your input helps us improve our services.";
      hasErrors = true;
    }

    // Always require email when submitting
    if (!email) {
      setErrors(prev => ({ ...prev, email: true }));
      errorMessage = hasErrors ? errorMessage + "\n\nPlease provide your email address." : "Please provide your email address.";
      hasErrors = true;
    } else if (!validateEmail(email)) {
      // Validate email format if provided
      setErrors(prev => ({ ...prev, email: true }));
      errorMessage = hasErrors ? errorMessage + "\n\nPlease enter a valid email address." : "Please enter a valid email address.";
      hasErrors = true;
    }

    if (hasErrors) {
      showErrorModal(errorMessage);
      return false;
    }
    
    return true;
  };

  // Form submission function
  const handleFormSubmission = () => {
    setIsSubmitting(true);
     
    // Stop the timer and get the elapsed time
    const timeElapsed = stopTimer();
    
    // Gather all form data
    const formData = {
      clientType,
      gender,
      age,
      region,
      office,
      subOffice,
      service,
      otherSpecification,
      ccAwareness,
      ccVisibility,
      ccHelpfulness,
      satisfaction,
      suggestions,
      email,
      privacyAgreement,
      timeSpent: timeElapsed,
      InternalExternal,
    };
    
    // Submit form data to API
    surveyApi.submitSurvey(formData)
      .then(response => {
        // Show success step
        setSubmissionComplete(true);
        setActiveStep(activeStep + 1);
        scrollToTop();
      })
      .catch(error => {
        if (error.validationErrors) {
          // Set validation errors returned from the server
          setErrors(prev => ({
            ...prev,
            ...error.validationErrors
          }));
          
          showErrorModal("Please correct the errors in your form.");
        } else {
          // Handle other errors
          setSubmissionError("An error occurred. Please try again later.");
          showErrorModal("An error occurred while submitting the form. Please try again.");
        }
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleNext = () => {
    // Reset all errors first
    setErrors({
      clientType: false,
      gender: false,
      age: false,
      region: false,
      office: false,
      subOffice: false,
      service: false,
      InternalExternal: false,
      otherSpecification: false,
      ccAwareness: false,
      ccVisibility: false,
      ccHelpfulness: false,
      satisfaction: false,
      suggestions: false,
      email: false
    });
  
    let hasErrors = false;
    let missingFields = [];
  
    // Validation for each step
    if (activeStep === 0 && !privacyAgreement) {
      showErrorModal("You must agree to the Data Privacy Notice to proceed.");
      return;
    } 
    else if (activeStep === 1) {
      // Personal info validation (unchanged)
      if (!clientType) {
        setErrors(prev => ({ ...prev, clientType: true }));
        missingFields.push("Client Type");
        hasErrors = true;
      }
      if (!gender) {
        setErrors(prev => ({ ...prev, gender: true }));
        missingFields.push("Gender");
        hasErrors = true;
      }
      if (!age) {
        setErrors(prev => ({ ...prev, age: true }));
        missingFields.push("Age");
        hasErrors = true;
      }
      if (!region) {
        setErrors(prev => ({ ...prev, region: true }));
        missingFields.push("Region");
        hasErrors = true;
      }
      
      if (hasErrors) {
        showErrorModal(`Please fill in all required fields: ${missingFields.join(", ")}`);
        return;
      }
    }  
    else if (activeStep === 2) {
      // Check InternalExternal selection first
      if (!InternalExternal) {
        setErrors(prev => ({ ...prev, InternalExternal: true }));
        missingFields.push("Client Type (Internal/External)");
        hasErrors = true;
      }
      
      // Check office selection
      if (!office) {
        setErrors(prev => ({ ...prev, office: true }));
        missingFields.push("Office");
        hasErrors = true;
      }
      
      // Check sub-office selection - required if the office has sub-offices
      if (office && hasSubOffices() && !subOffice) {
        setErrors(prev => ({ ...prev, subOffice: true }));
        missingFields.push("Sub-Office");
        hasErrors = true;
      }
      
      // Only check service selection if office (and sub-office if required) are selected
      const shouldCheckService = office && (!hasSubOffices() || (hasSubOffices() && subOffice));
      
      if (shouldCheckService && !service) {
        setErrors(prev => ({ ...prev, service: true }));
        missingFields.push("Service");
        hasErrors = true;
      }
      
      // Check "Others" specification if "Others" is selected
      const isOthersSelected = service && service.includes('_others');
      if (isOthersSelected && !otherSpecification) {
        setErrors(prev => ({ ...prev, otherSpecification: true }));
        missingFields.push("Service Specification");
        hasErrors = true;
      }
      
      if (hasErrors) {
        showErrorModal(`Please fill in the following required fields: ${missingFields.join(", ")}`);
        return;
      }
    }

    else if (activeStep === 3) {
      if (!ccAwareness) {
        setErrors(prev => ({ ...prev, ccAwareness: true }));
        missingFields.push("CC Awareness (CC1)");
        hasErrors = true;
      }
      
      // Only validate these if the user is aware of CC
      if (ccAwareness && ccAwareness !== "I do not know what a CC is and I did not see one in this office.") {
        if (!ccVisibility) {
          setErrors(prev => ({ ...prev, ccVisibility: true }));
          missingFields.push("CC Visibility (CC2)");
          hasErrors = true;
        }
        if (!ccHelpfulness) {
          setErrors(prev => ({ ...prev, ccHelpfulness: true }));
          missingFields.push("CC Helpfulness (CC3)");
          hasErrors = true;
        }
      } else if (ccAwareness === "I do not know what a CC is and I did not see one in this office.") {
        // If they don't know what a CC is, automatically set visibility and helpfulness to N/A
        if (!ccVisibility) setCCVisibility("N/A");
        if (!ccHelpfulness) setCCHelpfulness("N/A");
      }
      
      if (hasErrors) {
        showErrorModal(`Please answer the following questions: ${missingFields.join(", ")}`);
        return;
      }
    }
    else if (activeStep === 4) {
      // Check if all required satisfaction ratings are provided
      const satisfactionKeys = Object.keys(satisfaction);
      let missingSatisfactionRatings = false;
      
      for (const key of satisfactionKeys) {
        // Skip SQD5 if it's not payment-related and already set to N/A
        if (key === 'sqd5' && !isPaymentRelatedService() && satisfaction[key] === 'N/A') {
          continue;
        }
        
        if (!satisfaction[key]) {
          missingSatisfactionRatings = true;
          break;
        }
      }
      
      if (missingSatisfactionRatings) {
        setErrors(prev => ({ ...prev, satisfaction: true }));
        showErrorModal("Please provide ratings for all service quality dimensions.");
        return;
      }
    }
    else if (activeStep === 5) {
      // Feedback step validation (updated)
      if (!validateFeedbackStep()) {
        return;
      }
    }

    // If we reach here, there are no validation errors, so proceed to next step
    if (activeStep === steps.length - 1 && !hasErrors) {
      // Submit the form
      handleFormSubmission();
    } else if (!hasErrors) {
      // If not on the last step and no errors, proceed to next step
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      scrollToTop();
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    scrollToTop();  
  };

  const handleSatisfactionChange = (key, value) => {
    setSatisfaction(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Return appropriate step content based on active step
  const getStepContent = (step) => {

    if (submissionComplete) {
      return <ThankYouStep />;
    }

    switch (step) {
      case 0:
        return (
          <PrivacyNoticeStep 
            privacyAgreement={privacyAgreement} 
            setPrivacyAgreement={handlePrivacyAgreement} 
            themeColors={themeColors}
            startTimer={startTimer}
          />
        );
      case 1:
        return (
          <PersonalInfoStep 
            clientType={clientType}
            setClientType={setClientType}
            gender={gender}
            setGender={setGender}
            age={age}
            setAge={setAge}
            region={region}
            setRegion={setRegion}
            errors={errors}
            themeColors={themeColors}
          />
        );
      case 2:
        return (
          <ServiceAvailedStep 
            office={office}
            InternalExternal={InternalExternal}
            setInternalExternal={setInternalExternal}
            setOffice={setOffice}
            subOffice={subOffice}
            setSubOffice={setSubOffice}
            service={service}
            setService={setService}
            otherSpecification={otherSpecification}
            setOtherSpecification={setOtherSpecification}
            errors={errors}
            themeColors={themeColors}
          />
        );
      case 3:
        return (
          <CitizenCharterStep 
            ccAwareness={ccAwareness}
            setCCAwareness={setCCAwareness}
            ccVisibility={ccVisibility}
            setCCVisibility={setCCVisibility}
            ccHelpfulness={ccHelpfulness}
            setCCHelpfulness={setCCHelpfulness}
            errors={errors}
            themeColors={themeColors}
          />
        );
      case 4:
        return (
          <ServiceQualityStep 
            satisfaction={satisfaction}
            handleSatisfactionChange={handleSatisfactionChange}
            isPaymentRelatedService={isPaymentRelatedService}
            errors={errors}
            themeColors={themeColors}
          />
        );
      case 5:
        return (
          <FeedbackStep 
            suggestions={suggestions}
            setSuggestions={setSuggestions}
            email={email}
            setEmail={setEmail}
            clientType={clientType}
            InternalExternal={InternalExternal}
            gender={gender}
            age={age}
            region={region}
            office={office}
            subOffice={subOffice}
            service={service}
            otherSpecification={otherSpecification}
            ccAwareness={ccAwareness}
            ccVisibility={ccVisibility}
            ccHelpfulness={ccHelpfulness}
            satisfaction={satisfaction}
            activeStep={activeStep}
            steps={steps}
            isPaymentRelatedService={isPaymentRelatedService}
            themeColors={themeColors}
            errors={errors}
          />
        );
      case 6:
        return <ThankYouStep />;

      default:
        return 'Unknown step';
    }
  };

  return (
    <Container sx={{ width: { xs: '100%', sm: '90%', md: '75%', lg: '50%' } }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 0, 
          mt: 0, 
          mb: 4, 
          backgroundColor: '#800000', 
          textAlign: 'center',
          borderRadius: 0
        }}
      >
        <Box sx={{ display: 'flex', p: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
          <Box sx={{ flex: 2 }}>
            <Typography variant="h4" fontWeight="bold" color="white" 
              sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' } }}>
              DEPED REGIONAL OFFICE XI
            </Typography>
            <Box 
              sx={{ 
                bgcolor: '#FFFFF1', 
                p: 1, 
                borderRadius: 1, 
                mt: 1, 
                mx: 'auto',
                maxWidth: { xs: '300px', sm: '400px', md: '500px' }
              }}
            >
              <Typography variant="h5" fontWeight="bold" color="maroon" letterSpacing={2}
                sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }}>
                CLIENT SATISFACTION MEASUREMENT
              </Typography>
            </Box>
            <Typography variant="h6" color="white" mt={2}
              sx={{ fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' } }}>
              Help us serve you better!
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Main Form Container */}
      <Paper elevation={5} sx={{ p: { xs: 2, sm: 3, md: 4 }, mb: 4 }}>
        <Typography variant="h5" align="center" gutterBottom
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}>
          Client Feedback Form
        </Typography>
        
        {/* Stepper - Hide labels on mobile */}
        <Stepper 
          activeStep={activeStep} 
          alternativeLabel 
          sx={{ 
            mb: 3,
            '& .MuiStepLabel-label': {
              display: { xs: 'none', sm: 'block' }
            }
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ mb: 1 }}>
          {/* Introduction message for first step */}
          {activeStep === 0 && (
            <Typography variant="body1" sx={{ mb: 2, textAlign: 'justify', fontSize: { xs: '0.875rem', md: '1rem' } }}>
              This Client Satisfaction Measurement (CSM) tracks the customer experience of government offices. 
              Your feedback on your recently concluded transaction will help this office provide a better service. 
              Personal information shared will be kept confidential and you always have the option to not answer this form.
            </Typography>
          )}

          {/* Dynamic step content */}
          {getStepContent(activeStep)}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Navigation Buttons */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 }
        }}>
          <Button
            variant="contained"
            disabled={activeStep === 0 || isThankYouStep}
            onClick={handleBack}
            sx={{ 
              bgcolor: '#FFFFF1', 
              color: 'maroon',
              '&:hover': {
                bgcolor: '#800000',
                color: '#FFFFF1'
              },
              order: { xs: 2, sm: 1 },
              display: isThankYouStep ? 'none' : 'block' // Optional: completely hide the button on Thank You step
            }}
          >
            &lt;&lt;&lt; Prev
          </Button>
          
          <Button
            variant="contained"
            disabled={
              (activeStep === 0 && !privacyAgreement) || 
              isSubmitting || 
              isThankYouStep || 
              activeStep === steps.length
            }
            onClick={handleNext}
            sx={{ 
              bgcolor: (activeStep === 0 && !privacyAgreement) || isThankYouStep ? '#e0e0e0' : '#FFFFF1',
              color: (activeStep === 0 && !privacyAgreement) || isThankYouStep ? '#a0a0a0' : 'maroon',
              '&:hover': {
                bgcolor: (activeStep === 0 && !privacyAgreement) || isThankYouStep ? '#e0e0e0' : '#800000',
                color: (activeStep === 0 && !privacyAgreement) || isThankYouStep ? '#a0a0a0' : '#FFFFF1'
              },
              order: { xs: 1, sm: 2 }
            }}
          >
            {isSubmitting ? 'Submitting...' : activeStep === steps.length - 1 ? 'Submit' : 'Next >>>'}
          </Button>
        </Box>
      </Paper>

      {/* Error Dialog */}
      <ErrorDialog 
        open={openModal}
        setOpen={setOpenModal}
        message={modalMessage}
        themeColors={themeColors}
      />
    </Container>
  );
};

export default SurveyForm;